import { useRef } from "react";
/**
 * useStableArray
 *
 * Returns the same array reference across renders
 * if the contents are shallow-equal to the previous one.
 *
 * Why? In React, `.map` or spread always creates new arrays,
 * which breaks referential equality and can trigger unnecessary
 * effects/network calls. This hook preserves identity when values
 * haven’t actually changed.
 *
 * It doesn’t mutate the input — it only *reuses* the previous
 * array reference if nothing is different, so it stays compatible
 * with React’s immutable data philosophy.
 */
function useStableArray<T>(arr: T[]): T[] {
  const ref = useRef(arr);
  if (
    ref.current.length !== arr.length ||
    ref.current.some((v, i) => v !== arr[i])
  ) {
    ref.current = arr;
  }
  return ref.current;
}

export default useStableArray;
